import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router";
import {
  Search,
  ChevronDown,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";
import { SECTION_ICONS } from "@/lib/section-icons";
import { useHasNewChangelog } from "@/hooks/use-updates";
import { useAllowedPages, useIsAdmin, useRevokedPages } from "@/hooks/use-rbac";
import { useTheme } from "@/components/layout/ThemeProvider";
import { enabledThemeClasses } from "@/lib/themes";
import { isDarkRail, railLogoSrc } from "@/lib/rail-logo";
import { usePreferences } from "@/hooks/use-settings";
import { openCommandPalette } from "@/components/CommandPalette";
import { canonicalPath, isPathAllowed } from "@shared/rbac";
import { visibleSidebarPages } from "@shared/sidebar-visibility";
import {
  SIDEBAR_SECTIONS,
  ALL_PAGE_PATHS,
  sectionKeyForPath,
  type SidebarPage,
  type SidebarSection,
} from "@shared/sidebar-pages";
import { parseUiPrefs, extractCollapsed } from "@shared/ui-prefs";
import { navResetState } from "@shared/nav-reset";
import { MyLinksSection } from "./MyLinks";
import {
  SECTION_COLLAPSED_KEY,
  moduleRowClass,
  railIconClass,
  readCollapsedSections,
  subItemClass,
  writeCollapsedSection,
} from "./sidebar-shared";

// SECTION_ICONS (one icon per module, #914) moved to @/lib/section-icons — shared
// with the command palette, which derives its page list from the same catalog.

/**
 * #914 group labels — Cloudflare's Observe/Build tier. PRESENTATION-ONLY clustering
 * of catalog sections into labelled clusters; the catalog (order, RBAC, labels) is
 * untouched. Keys must cover every SIDEBAR_SECTIONS key in catalog order — the
 * coverage test (tests/route-catalog-coverage.test.ts) parses this block and asserts
 * it. A section missing from here still renders (appended unlabelled at the end),
 * so a forgotten entry degrades visibly, never invisibly.
 */
const MODULE_GROUPS: { label: string | null; keys: string[] }[] = [
  { label: null, keys: ["overview"] },
  { label: "Plant", keys: ["production", "quality", "lab"] },
  { label: "Innovation", keys: ["rd", "projects"] },
  { label: "Business", keys: ["analytics", "commercial"] },
  { label: "Workspace", keys: ["tools", "internal"] },
];

interface NavItem {
  to: string;
  label: string;
  badge?: React.ReactNode;
  /** If true, renders as a sub-divider label instead of a nav link */
  divider?: boolean;
  /** NavLink `end` — set when another catalog page nests under this path, so the
   * parent (/admin) doesn't stay highlighted on the child (/admin/material-mapping). */
  end?: boolean;
  /** #2064: catalog `parent` path — the item renders nested under it when that parent is visible. */
  parent?: string;
  /** #2545: catalog `adminOnly` — nav row renders for admins only (see filterItems). */
  adminOnly?: boolean;
}

/**
 * Project a catalog section's pages into the NavItem[] the renderer consumes:
 * skip navHidden pages, carry dividers through, and attach the /updates "new changelog"
 * badge when present. Order is preserved 1:1 from the catalog (which IS the visible
 * nav order — see sidebar-pages.ts). Sub-items are text-only by design (#914).
 */
function sectionToNavItems(pages: SidebarPage[], hasNew: boolean): NavItem[] {
  const items: NavItem[] = [];
  for (const page of pages) {
    if (page.divider) {
      items.push({ divider: true, label: page.label, to: "" });
      continue;
    }
    if (page.navHidden) continue;
    items.push({
      to: page.path,
      label: page.label,
      badge: page.path === "/updates" && hasNew ? NewBadge : undefined,
      end:
        page.path === "/" ||
        ALL_PAGE_PATHS.some((p) => p !== page.path && p.startsWith(page.path + "/")),
      parent: page.parent,
      adminOnly: page.adminOnly,
    });
  }
  return items;
}

/**
 * #2064: a child nests only when its parent survived RBAC filtering — a role that
 * holds the child alone gets a plain row, never an indent under a missing label.
 */
function isNested(item: NavItem, items: NavItem[]): boolean {
  return item.parent != null && items.some((i) => !i.divider && i.to === item.parent);
}

function navItemIsActive(item: NavItem, pathname: string): boolean {
  if (item.divider) return false;
  if (item.end) return canonicalPath(pathname) === canonicalPath(item.to);
  return isPathAllowed(pathname, [item.to]);
}

/**
 * A rail sub-item — matched to the kumo markup in Jez's saved Cloudflare page
 * (menu-sub-button: `gap-2 rounded-lg min-h-8.5 px-3 py-0 text-sm font-medium`,
 * neutral hover bg, default-not-muted text). No colour accents: the active page
 * takes the same grey pill, the rail line is plain and continuous. The pill starts
 * a step right of the rail (`ml-2`, rail stays visible) and the text column
 * (ml-2 + px-3 = 20px past the 20px rail) aligns with the module labels above
 * (px-3 + 16px icon + gap-3 = 40px).
 */
function SubItem({
  item,
  onClose,
  navyOn,
  nested = false,
}: {
  item: NavItem;
  onClose: () => void;
  navyOn: boolean;
  /** #2064: one extra indent step — the item lives inside the page above it. */
  nested?: boolean;
}) {
  const { pathname } = useLocation();
  if (item.divider) {
    return (
      <p
        className={`ml-2 pb-0.5 pl-3 pt-2.5 text-[10px] font-medium uppercase tracking-wider ${
          navyOn ? "text-muted-foreground/75" : "text-muted-foreground/60"
        }`}
      >
        {item.label}
      </p>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      state={navResetState()} // #1986: sidebar click = landing state
      onClick={onClose}
      className={({ isActive }) => subItemClass(isActive || navItemIsActive(item, pathname), nested)}
    >
      {item.label}
      {item.badge}
    </NavLink>
  );
}

/**
 * Expanded-rail module: icon + label header that folds its pages out (chevron), pages
 * as text-only sub-items on the rail. The module containing the active route auto-opens
 * on navigation (Cloudflare behaviour); a manual fold while active sticks until the
 * user navigates INTO the section again. Fold state persists per label (localStorage +
 * ui_prefs, #373) — absence now means FOLDED: the whole point of #914 is a short rail
 * of modules, not ten open sections.
 */
function NavSection({
  label,
  icon: Icon,
  items,
  onClose,
  sectionActive,
  navyOn,
}: {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  onClose: () => void;
  sectionActive: boolean;
  navyOn: boolean;
}) {
  const [expanded, setExpanded] = useState(() => {
    const stored = readCollapsedSections()[label];
    if (stored === true) return false; // user folded it
    if (stored === false) return true; // user opened it
    return sectionActive; // no preference: open only around the active route
  });
  useEffect(() => {
    // Navigating into the section opens it; only the false→true flip re-fires, so a
    // manual fold WHILE inside the section isn't fought by this effect.
    if (sectionActive) setExpanded(true);
  }, [sectionActive]);

  const realItems = items.filter((i) => !i.divider);
  if (realItems.length === 0) return null;

  const toggle = () =>
    setExpanded((prev) => {
      writeCollapsedSection(label, prev); // prev=true (open) → store collapsed=true
      return !prev;
    });
  const hasBadge = realItems.some((i) => i.badge);

  return (
    <div className="mb-0.5">
      <button type="button" onClick={toggle} aria-expanded={expanded} className={moduleRowClass(sectionActive && !expanded)}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left">{label}</span>
        {!expanded && hasBadge && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />}
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
        />
      </button>
      {expanded && (
        // Rail line sits at the module icon's centre-x (px-3 + half a 16px icon = 20px).
        <div className="mb-1 ml-5 mt-0.5 space-y-px border-l border-sidebar-border">
          {items.map((item) => (
            <SubItem
              key={item.divider ? `div-${item.label}` : item.to}
              item={item}
              onClose={onClose}
              navyOn={navyOn}
              nested={isNested(item, items)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsed-rail module: one icon; hover (or focus) flies out a panel listing the
 * section's pages, click navigates to the first page. The panel is portaled to <body>
 * with fixed positioning — the nav column is overflow-y-auto and the aside carries a
 * transform (both would clip/trap an in-tree fixed panel).
 */
function CollapsedSection({
  label,
  icon: Icon,
  items,
  sectionActive,
  onClose,
}: {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  sectionActive: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fly, setFly] = useState<{ top: number; left: number } | null>(null);

  const realItems = items.filter((i) => !i.divider);
  if (realItems.length === 0) return null;

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Clamp so a tall panel (Quality: 12 pages) never runs off the bottom edge.
    const maxPanel = Math.min(window.innerHeight * 0.7, 480);
    setFly({ top: Math.max(8, Math.min(rect.top, window.innerHeight - maxPanel - 8)), left: rect.right + 6 });
  };
  const hide = () => {
    hideTimer.current = setTimeout(() => setFly(null), 150);
  };
  const closeAll = () => {
    setFly(null);
    onClose();
  };
  const hasBadge = realItems.some((i) => i.badge);

  return (
    <div onMouseEnter={show} onMouseLeave={hide}>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => {
          navigate(realItems[0].to, { state: navResetState() }); // #1986
          closeAll();
        }}
        onFocus={show}
        onBlur={hide}
        title={label}
        className={railIconClass(sectionActive)}
      >
        <span className="relative">
          <Icon className="h-4 w-4" />
          {hasBadge && (
            <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-blue-500" />
          )}
        </span>
      </button>
      {fly &&
        createPortal(
          <div
            style={{ top: fly.top, left: fly.left, maxHeight: "70vh" }}
            className="fixed z-[60] min-w-44 overflow-y-auto rounded-lg border border-border bg-popover p-1.5 shadow-lg"
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {label}
            </p>
            {items.map((item) =>
              item.divider ? (
                <p
                  key={`div-${item.label}`}
                  className="px-2 pb-0.5 pt-1.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground/50"
                >
                  {item.label}
                </p>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  state={navResetState()} // #1986: sidebar click = landing state
                  onClick={closeAll}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md ${isNested(item, items) ? "ml-3 " : ""}px-2 py-1.5 text-sm font-medium transition-colors ${
                      isActive || navItemIsActive(item, pathname)
                        ? "bg-accent text-accent-foreground"
                        : "text-foreground/75 hover:bg-accent/60 hover:text-foreground"
                    }`
                  }
                >
                  {item.label}
                  {item.badge}
                </NavLink>
              )
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const COLLAPSED_KEY = "crosbe-sidebar-collapsed";

const NewBadge = <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />;

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export function Sidebar({ open, onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  // The lock below must not apply at lg+, where the aside is `lg:static` — a
  // normal sidebar, not a drawer. Without this guard, opening the drawer on
  // mobile and then crossing the breakpoint (iPad rotation, window resize)
  // leaves the body position:fixed on a layout with no overlay left to close
  // it: the page is frozen and nothing on screen explains why. Tracks the
  // breakpoint live so crossing lg releases the lock and crossing back
  // re-applies it while `open` is still true.
  const [isLgViewport, setIsLgViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsLgViewport(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Mobile drawer scroll-chaining fix, part 1 — lock the page behind while
  // the drawer is open. useLayoutEffect (not useEffect) commits synchronously in
  // the SAME paint as the drawer's open-state render, so there is no frame where
  // the drawer is visibly open without the lock applied — the gap that made this
  // worst on first load, when useEffect callbacks queue behind other mount work.
  // `position: fixed` (not just `overflow: hidden`) is the robust cross-iOS-version
  // technique: plain overflow:hidden on body is not reliably honoured by iOS
  // Safari's touch-scroll/rubber-band handling. Part 2 (overscroll-contain on the
  // drawer's own scroll container below) is CSS-only and needs no JS at all, so it
  // still holds even in the window before this effect has run.
  useLayoutEffect(() => {
    if (!open || isLgViewport) return;
    const { style } = document.body;
    const scrollY = window.scrollY;
    const prev = { overflow: style.overflow, position: style.position, top: style.top, width: style.width };
    style.overflow = "hidden";
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.width = "100%";
    return () => {
      style.overflow = prev.overflow;
      style.position = prev.position;
      style.top = prev.top;
      style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open, isLgViewport]);

  const hasNew = useHasNewChangelog();
  const allowedPages = useAllowedPages();
  const isAdmin = useIsAdmin();
  const revokedPages = useRevokedPages();
  const { pathname } = useLocation();
  const activeSectionKey = sectionKeyForPath(pathname);

  // #809 app theme registry — every theme is default OFF: with no flag stored,
  // `themeClasses` is "" and the rail renders exactly as prod does today. The
  // scoped theme class(es) come from the registry (APP_THEMES), never hardcoded
  // here, so future themes light up by adding a registry entry only.
  const { appThemes, resolvedTheme } = useTheme();
  // Sidebar-scoped themes only — root-scoped themes (e.g. the full-surface navy)
  // mount on <html> via ThemeProvider, not here.
  const themeClasses = enabledThemeClasses(appThemes, "sidebar");
  // Contrast boost applies to BOTH navy-sidebar themes: crosbe-navy-1 AND brand-1
  // (whose sidebar is also a navy gradient) — the /40 divider opacity is illegible
  // on navy either way (#914 staging screenshot pass caught brand-1 missing it).
  const navyOn = appThemes.crosbeNavy1 === true || appThemes.crosbeBrand1 === true;
  // The mobile drawer always renders the full (expanded) layout — a 16rem drawer of
  // centred icons is the worst of both worlds. `collapsed` only bites on the lg rail.
  const railCollapsed = collapsed && !open;

  // Logo by RAIL colour and width (lib/rail-logo): every dark rail (dark mode,
  // either navy theme, brand-1's navy sidebar) takes the official white wordmark
  // expanded and the white and light-blue mark collapsed (#3703); only the plain
  // light rail takes the navy wordmark and the full-colour mark. #3734: dark
  // mode is the RESOLVED theme, so "system" on a dark OS counts as dark too.
  const darkRail = isDarkRail(resolvedTheme, appThemes);
  const wordmarkSrc = railLogoSrc({ collapsed: false, darkRail });
  const markSrc = railLogoSrc({ collapsed: true, darkRail });

  // #373: hydrate per-section collapse from the user's server-side ui_prefs (cross-device).
  // Server wins → seed localStorage, then bump `prefsVersion` to remount the nav so each
  // NavSection re-reads the synced state. Fires once when the prefs query resolves; our own
  // writes don't invalidate the query, so there's no remount loop.
  const { data: prefs } = usePreferences();
  const [prefsVersion, setPrefsVersion] = useState(0);
  useEffect(() => {
    if (!prefs?.ui_prefs) return;
    const collapsedMap = extractCollapsed(parseUiPrefs(prefs.ui_prefs));
    if (collapsedMap) {
      localStorage.setItem(SECTION_COLLAPSED_KEY, JSON.stringify(collapsedMap));
      setPrefsVersion((v) => v + 1);
    }
  }, [prefs?.ui_prefs]);

  // Filter nav items based on user's role (null = still loading, show nothing)
  // Also hide admin-only pages for non-admin users

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar — h-dvh, NOT inset-y-0/100vh: on iOS Safari the layout viewport
          extends under the dynamic bottom toolbar, so a bottom-anchored drawer puts
          its last buttons where taps hit the toolbar instead (#1901, Akira on a
          live device). 100dvh tracks the VISIBLE viewport. lg:static keeps the
          desktop rail in-flow where the shell's h-dvh governs. The safe-area
          padding (#1901 reopened) keeps the last nav row above the home
          indicator once the toolbar has collapsed and dvh reaches the screen edge. */}
      <aside
        className={`
          standalone-top-inset fixed top-0 left-0 h-dvh pb-[env(safe-area-inset-bottom)] z-50 flex flex-col border-r border-sidebar-border bg-sidebar
          transition-all duration-200 ease-in-out print:hidden
          lg:static lg:translate-x-0
          ${open ? "translate-x-0 w-64" : "-translate-x-full w-64"}
          ${railCollapsed ? "lg:w-14" : "lg:w-64"}
          ${themeClasses}
        `}
      >
        {/* Logo / brand */}
        <div className={`flex h-14 items-center ${railCollapsed ? "lg:justify-center lg:px-2" : ""} justify-between px-6`}>
          {/* #809 crosbe-navy-1: the `crosbeAI` wordmark treatment is part of Akira's
              re-skin (recovered from staging verbatim) — conditional so the default
              (theme off) wordmark renders exactly as prod does today. */}
          {!railCollapsed && (
            <div className="flex items-start gap-0">
              <img src={wordmarkSrc} alt="Crosbe" className="h-7 w-auto object-contain shrink-0" />
              {/* Superscript AI (Akira 16 Jul: e→AI gap should equal the wordmark's internal
                  logo→c gap). Measured from the PNG: 600px wide, last glyph ends at 508,
                  logo→c gap 26px; at h-7 (scale 28/144) the 92px trailing padding renders
                  ~17.9px, so a 13px pull-in leaves ~4.9px ≈ the 5.1px logo→c gap. */}
              <span className="-ml-[13px] mt-0.5 text-[10px] font-bold leading-none text-[#4ab8f0]">AI</span>
            </div>
          )}
          {railCollapsed && (
            <img src={markSrc} alt="Crosbe" className="h-8 w-8 object-contain" />
          )}
          {/* #4334: named, and a 44 px hit area around the 20 px icon; -mr-3 keeps the
              icon where it sat against the px-6 edge. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="-mr-3 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Quick search — the sidebar affordance for the existing Cmd/Ctrl+K palette (#914).
            Sized per the Cloudflare reference: h-8 px-3 text-sm pill, plain ⌘K hint, on the
            lg rail; 44 px in the phone drawer (#4334). */}
        {!railCollapsed ? (
          <button
            type="button"
            onClick={openCommandPalette}
            className="mx-3 mb-1 flex h-11 items-center gap-2 rounded-lg border border-sidebar-border px-3 text-sm font-normal text-muted-foreground transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground lg:h-8"
          >
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 truncate text-left">Quick search...</span>
            <span className="shrink-0 text-xs text-muted-foreground/70">{isMac ? "⌘K" : "Ctrl K"}</span>
          </button>
        ) : (
          <div className="mb-2 px-1.5">
            <button
              type="button"
              onClick={openCommandPalette}
              title={`Quick search (${isMac ? "⌘K" : "Ctrl+K"})`}
              className={railIconClass(false)}
            >
              <Search className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Nav — one module per catalog section (#599 single source of truth): icon +
            fold-out when expanded, icon + hover fly-out when collapsed (#914). Modules
            render in labelled clusters (MODULE_GROUPS — Cloudflare's Observe/Build tier). */}
        {/* overscroll-contain (part 2 of the mobile drawer scroll-chaining fix): stops a
            touch-scroll that hits the top/bottom of the nav list from chaining to
            whatever scrollable ancestor sits behind the drawer. Unlike the body
            lock above, this is pure CSS present from this element's very first
            render — no JS/effect has to run for it to take effect. */}
        <nav key={prefsVersion} className={`flex-1 overflow-y-auto overscroll-contain ${railCollapsed ? "px-1.5" : "px-3"} pb-4`}>
          {(() => {
          const renderSection = (section: SidebarSection) => {
            // adminOnly sections (e.g. "Work In Progress") render only for admins.
            if (section.adminOnly && !isAdmin) return null;
            const icon = SECTION_ICONS[section.key];
            if (!icon) return null; // defensive: a section with no icon is skipped, not crashed
            const items = sectionToNavItems(visibleSidebarPages(section, allowedPages, isAdmin, revokedPages), hasNew);
            const realItems = items.filter((i) => !i.divider);
            if (realItems.length === 0) return null;
            const sectionActive = activeSectionKey === section.key;

            if (railCollapsed) {
              // Single-page module: the icon IS the link (no one-item fly-out).
              if (realItems.length === 1) {
                const only = realItems[0];
                const Icon = icon;
                return (
                  <NavLink
                    key={section.key}
                    to={only.to}
                    end={only.end}
                    state={navResetState()} // #1986: sidebar click = landing state
                    onClick={onClose}
                    title={only.label}
                    className={({ isActive }) => `mb-0.5 ${railIconClass(isActive)}`}
                  >
                    <span className="relative">
                      <Icon className="h-4 w-4" />
                      {only.badge}
                    </span>
                  </NavLink>
                );
              }
              return (
                <CollapsedSection
                  key={section.key}
                  label={section.label}
                  icon={icon}
                  items={items}
                  sectionActive={sectionActive}
                  onClose={onClose}
                />
              );
            }

            // Expanded rail. Single-page module renders as a direct link row.
            if (realItems.length === 1) {
              const only = realItems[0];
              const Icon = icon;
              return (
                <NavLink
                  key={section.key}
                  to={only.to}
                  end={only.end}
                  state={navResetState()} // #1986: sidebar click = landing state
                  onClick={onClose}
                  className={({ isActive }) => `mb-0.5 ${moduleRowClass(isActive)}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate text-left">{only.label}</span>
                  {only.badge}
                </NavLink>
              );
            }
            return (
              <NavSection
                key={section.key}
                label={section.label}
                icon={icon}
                items={items}
                onClose={onClose}
                sectionActive={sectionActive}
                navyOn={navyOn}
              />
            );
          };

          // Cluster the sections under their group labels. Any catalog section not
          // named in MODULE_GROUPS still renders — unlabelled, at the end.
          const byKey = new Map(SIDEBAR_SECTIONS.map((s) => [s.key, s]));
          const grouped = MODULE_GROUPS.map((g) => ({
            label: g.label,
            sections: g.keys
              .map((k) => byKey.get(k))
              .filter((s): s is SidebarSection => Boolean(s)),
          }));
          const knownKeys = new Set(MODULE_GROUPS.flatMap((g) => g.keys));
          const leftovers = SIDEBAR_SECTIONS.filter((s) => !knownKeys.has(s.key));
          if (leftovers.length) grouped.push({ label: null, sections: leftovers });

          return grouped.map((group, gi) => {
            const rendered = group.sections
              .map((s) => renderSection(s))
              .filter((el): el is React.ReactElement => el !== null);
            // RBAC can empty a whole cluster — skip its label too, not just its rows.
            if (rendered.length === 0) return null;
            return (
              <div key={group.label ?? `group-${gi}`}>
                {group.label && !railCollapsed && (
                  <p className="mb-1 mt-4 truncate px-3 text-sm font-medium text-muted-foreground">
                    {group.label}
                  </p>
                )}
                {group.label && railCollapsed && (
                  <div className="mx-2 my-2 border-t border-sidebar-border" />
                )}
                {rendered}
              </div>
            );
          });
          })()}
          {/* #2089: the user's own module, last on the rail, under the same divider the clusters use. */}
          {railCollapsed ? (
            <div className="mx-2 my-2 border-t border-sidebar-border" />
          ) : (
            <div className="mb-1 mt-4 border-t border-sidebar-border" />
          )}
          <MyLinksSection railCollapsed={railCollapsed} onClose={onClose} />
        </nav>

        {/* Footer with collapse toggle */}
        <div className={`border-t border-sidebar-border ${railCollapsed ? "p-2" : "p-4"}`}>
          <div className={`flex items-center ${railCollapsed ? "justify-center" : "justify-between"}`}>
            {!railCollapsed && (
              <p className="text-xs text-muted-foreground">
                Crosbe AI v2.5
              </p>
            )}
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
              title={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {railCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}


/** Paper menu shares the sidebar's catalog and permission filter, including
 * collapsed sections, without changing the user's navigation preferences. */
export function PrintNavigation() {
  const allowedPages = useAllowedPages();
  const isAdmin = useIsAdmin();
  const revokedPages = useRevokedPages();
  return <nav className="hidden" data-qctds-paper-navigation aria-label="Printed navigation">
    {SIDEBAR_SECTIONS.map((section) => {
      if (section.adminOnly && !isAdmin || !SECTION_ICONS[section.key]) return null;
      const items = sectionToNavItems(visibleSidebarPages(section, allowedPages, isAdmin, revokedPages), false).filter((item) => !item.divider);
      if (!items.length) return null;
      return <div key={section.key}><strong>{section.label}</strong><ul>{items.map((item) => <li key={item.to}><a href={item.to}>{item.label}</a></li>)}</ul></div>;
    })}
  </nav>;
}
